/**
 * WishlistProvider — client-side wishlist storage.
 *
 * Shopify has no native wishlist object, so this persists to localStorage
 * (per-browser, not synced to the customer's account). Each entry is a
 * small snapshot of real Shopify product data captured at the moment a
 * customer saves it (handle/title/image/price/url) — never fabricated.
 *
 * Upgrade path: to sync wishlists to the logged-in customer across devices,
 * swap this module's storage calls for a customer metafield read/write via
 * a Shopify app (Admin API access needed, which a theme alone can't do) —
 * every call site below (`toggle`, `has`, `getAll`) is the seam to redirect.
 */
window.WishlistProvider = (() => {
  const STORAGE_KEY = 'bloom_wishlist';

  function getAll() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      return [];
    }
  }

  function save(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items } }));
  }

  function has(handle) {
    return getAll().some((item) => item.handle === handle);
  }

  function add(product) {
    const items = getAll();
    if (items.some((item) => item.handle === product.handle)) return items;
    items.push(product);
    save(items);
    return items;
  }

  function remove(handle) {
    const items = getAll().filter((item) => item.handle !== handle);
    save(items);
    return items;
  }

  function toggle(product) {
    return has(product.handle) ? remove(product.handle) : add(product);
  }

  function count() {
    return getAll().length;
  }

  return { getAll, has, add, remove, toggle, count };
})();

(() => {
  function updateBadges() {
    const count = window.WishlistProvider.count();
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function updateButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const handle = button.dataset.wishlistToggle;
      const saved = window.WishlistProvider.has(handle);
      button.setAttribute('aria-pressed', String(saved));
      button.classList.toggle('is-saved', saved);
    });
  }

  function init() {
    updateBadges();
    updateButtons();

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wishlist-toggle]');
      if (!button) return;

      event.preventDefault();
      window.WishlistProvider.toggle({
        handle: button.dataset.wishlistToggle,
        title: button.dataset.wishlistTitle,
        image: button.dataset.wishlistImage,
        price: button.dataset.wishlistPrice,
        url: button.dataset.wishlistUrl,
      });
    });

    document.addEventListener('wishlist:change', () => {
      updateBadges();
      updateButtons();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
